'use client';

import { Children, createContext, use, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '@workspace/ui/lib/utils';

export type ToastType = 'success' | 'error' | 'warning' | 'info';
export type ToastActions = ReactNode[] | ReactNode;

export interface ToastOptions {
    type: ToastType;
    /** 主文案；超出视口宽度时换行，窄屏不溢出。 */
    message: string;
    /** 主文案下方的补充说明。 */
    description?: string;
    /** 操作入口；点击任一操作后关闭这条提示。 */
    actionList?: ToastActions;
    /** 自动关闭时间（毫秒），0 表示不自动关闭。 */
    duration?: number;
    /** `top-center` 是操作反馈的默认位置；`bottom-right` 留给不打断当前操作的完成通知。 */
    position?: 'top-center' | 'bottom-right';
    /** `compact` 用于随操作即时出现的轻量反馈。 */
    size?: 'default' | 'compact';
}

interface ToastContextValue {
    showToast: (options: ToastOptions) => void;
    showSuccess: (message: string, actionList?: ToastActions) => void;
    showError: (message: string, actionList?: ToastActions) => void;
    showWarning: (message: string, actionList?: ToastActions) => void;
    showInfo: (message: string, actionList?: ToastActions) => void;
}

interface ToastEntry extends ToastOptions {
    id: string;
}

const DEFAULT_DURATION_MS = 5000;

const ToastContext = createContext<ToastContextValue | null>(null);

// 颜色全部取主题 Token，深浅色模式随宿主主题切换。
const ICONS: Record<ToastType, ReactNode> = {
    success: (
        <svg width="21" height="21" viewBox="0 0 21 21" fill="none" aria-hidden="true">
            <rect x="0.115723" y="0.958984" width="20" height="20" rx="10" fill="var(--success)" />
            <path d="M8.23689 15.5693L3.86572 11.1981L4.95851 10.1053L8.23689 13.3837L15.2729 6.34766L16.3657 7.44045L8.23689 15.5693Z" fill="var(--primary-foreground)" />
        </svg>
    ),
    error: (
        <svg width="20" height="21" viewBox="0 0 20 21" fill="none" aria-hidden="true">
            <rect y="0.75" width="20" height="20" rx="10" fill="var(--error)" />
            <path d="M7.48246 14.25L6.5 13.2675L9.02632 10.7412L6.5 8.23246L7.48246 7.25L10.0088 9.77632L12.5175 7.25L13.5 8.23246L10.9737 10.7412L13.5 13.2675L12.5175 14.25L10.0088 11.7237L7.48246 14.25Z" fill="var(--destructive-foreground)" />
        </svg>
    ),
    warning: (
        <svg width="21" height="21" viewBox="0 0 21 21" fill="none" aria-hidden="true">
            <path d="M8.79223 2.18466C9.57166 0.905879 11.4283 0.905881 12.2078 2.18466L19.0388 13.3919C19.8512 14.7247 18.8919 16.4329 17.331 16.4329H3.66896C2.10814 16.4329 1.14885 14.7247 1.96119 13.3919L8.79223 2.18466Z" fill="var(--alert)" />
            <path d="M9.83301 11.0693V5.06934H11.1663V11.0693H9.83301ZM9.83301 14.4027V13.0693H11.1663V14.4027H9.83301Z" fill="var(--primary-foreground)" />
        </svg>
    ),
    info: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <rect width="20" height="20" rx="10" fill="var(--info)" />
            <path d="M9.33301 11.333V5.33301H10.6663V11.333H9.33301ZM9.33301 14.6663V13.333H10.6663V14.6663H9.33301Z" fill="var(--primary-foreground)" />
        </svg>
    ),
};

function ToastItem({ toast, onDismiss }: { toast: ToastEntry; onDismiss: (id: string) => void }) {
    const { id, type, message, description, actionList, duration = DEFAULT_DURATION_MS } = toast;
    const compact = toast.size === 'compact';
    const actions = Children.toArray(actionList);

    useEffect(() => {
        if (duration <= 0) return;
        const timer = setTimeout(() => onDismiss(id), duration);
        return () => clearTimeout(timer);
    }, [duration, id, onDismiss]);

    return (
        <div
            className={cn(
                // 宽度随内容，超出视口或 40rem 时换行，保证窄屏与长文案都可读。
                'pointer-events-auto flex w-max max-w-full items-start gap-2 border border-border bg-popover text-popover-foreground shadow-[var(--shadow-toast)] sm:max-w-[40rem]',
                compact ? 'min-h-12 rounded-[10px] px-4 py-3' : 'min-h-[52px] rounded-[14px] p-4',
            )}
        >
            <span className={cn('flex h-[22px] shrink-0 items-center', compact && '[&_svg]:size-4')}>{ICONS[type]}</span>
            <div className="min-w-0">
                <div className={cn('flex flex-wrap items-center gap-y-1', compact ? 'gap-x-1.5' : 'gap-x-3')}>
                    <p className="min-w-0 break-words text-sm font-medium leading-[22px]">{message}</p>
                    {actions.length > 0 ? (
                        <div className="flex shrink-0 items-center gap-2">
                            {actions.map((action, index) => (
                                // 操作完成后提示即失去意义，点击冒泡到这里统一关闭。
                                <div key={index} className="flex cursor-pointer items-center" onClick={() => onDismiss(id)}>
                                    {action}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
                {description ? <p className="mt-0.5 break-words text-sm text-muted-foreground">{description}</p> : null}
            </div>
        </div>
    );
}

export function ToastProvider({ children }: { /** 应用子树。 */ children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastEntry[]>([]);
    const prefix = useId();
    const nextId = useRef(0);

    const dismiss = useCallback((id: string) => {
        setToasts((current) => current.filter((toast) => toast.id !== id));
    }, []);

    const showToast = useCallback((options: ToastOptions) => {
        const id = `${prefix}-${++nextId.current}`;
        setToasts((current) => [...current, { ...options, id }]);
    }, [prefix]);

    const value = useMemo<ToastContextValue>(() => ({
        showToast,
        showSuccess: (message, actionList) => showToast({ type: 'success', message, actionList }),
        showError: (message, actionList) => showToast({ type: 'error', message, actionList }),
        showWarning: (message, actionList) => showToast({ type: 'warning', message, actionList }),
        showInfo: (message, actionList) => showToast({ type: 'info', message, actionList }),
    }), [showToast]);

    const bottomRight = toasts.filter((toast) => toast.position === 'bottom-right');
    const topCenter = toasts.filter((toast) => toast.position !== 'bottom-right');

    return (
        <ToastContext value={value}>
            {children}
            <div aria-live="polite" className="pointer-events-none fixed inset-x-4 top-4 z-[10000] flex flex-col items-center gap-2">
                {topCenter.map((toast) => <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />)}
            </div>
            <div aria-live="polite" className="pointer-events-none fixed bottom-4 left-4 right-4 z-[10000] flex flex-col items-end gap-2">
                {bottomRight.map((toast) => <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />)}
            </div>
        </ToastContext>
    );
}

export function useToast(): ToastContextValue {
    const context = use(ToastContext);
    if (!context) throw new Error('useToast must be used within a ToastProvider');
    return context;
}
